import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import CarrierSetupDialog, { isSetupError } from "@/components/CarrierSetupDialog";
import { Package, Route, Star, Loader2, Send, Timer, ArrowUpDown } from "lucide-react";
import { sizeLabel } from "@/lib/sizes";

const AvailableParcels = ({ tripId, onClearTrip }) => {
  const [items, setItems] = useState([]);
  const [sort, setSort] = useState("departure");
  const [loading, setLoading] = useState(false);
  const [offering, setOffering] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/parcels/for-carrier?sort=${sort}${tripId ? `&trip_id=${tripId}` : ""}`)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [sort, tripId]);
  useEffect(() => { load(); }, [load]);

  const [setupTrip, setSetupTrip] = useState(null);
  const offer = async (row) => {
    setOffering(row.parcel.id);
    try {
      await api.post("/requests", {
        parcel_id: row.parcel.id,
        trip_id: row.trip.id,
        initiated_by: "carrier",
      });
      toast.success("Offer sent to sender · they have 30 minutes to respond.");
      load();
    } catch (e) {
      if (isSetupError(e)) setSetupTrip(row.trip.id);
      else toast.error(e?.response?.data?.detail || "Couldn't send the offer.");
    } finally { setOffering(null); }
  };

  return (
    <div className="space-y-3" data-testid="available-parcels">
      <CarrierSetupDialog tripId={setupTrip} open={!!setupTrip} onClose={() => setSetupTrip(null)}/>
      <div className="flex items-center gap-2 justify-between">
        <div>
          <h2 className="font-display text-lg font-bold text-slate-900">Parcels near your route</h2>
          <p className="text-xs text-slate-500">
            Shown only when they're within the configured detour of one of your active trips.
          </p>
          {tripId && (
            <div className="mt-1.5 inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 text-amber-900 text-[11px] px-2.5 py-0.5" data-testid="trip-filter-chip">
              Showing parcels for the trip you just posted
              <button onClick={onClearTrip} className="underline" data-testid="clear-trip-filter">Show all trips</button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 text-xs text-slate-500">
          <ArrowUpDown className="w-3.5 h-3.5"/>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="rounded-full h-8 w-40 text-xs"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="departure">Departure · soonest</SelectItem>
              <SelectItem value="detour">Detour · shortest</SelectItem>
              <SelectItem value="price">Earnings · high to low</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading && (
        <div className="rounded-2xl bg-white border p-6 text-center text-slate-500 inline-flex items-center gap-2 justify-center w-full">
          <Loader2 className="w-4 h-4 animate-spin"/> Looking for matches…
        </div>
      )}
      {!loading && items.length === 0 && (
        <div className="rounded-2xl bg-white border p-8 text-center">
          <Package className="w-10 h-10 mx-auto text-slate-300 mb-2"/>
          <div className="font-display font-bold text-slate-900 mb-1">No matching parcels right now</div>
          <div className="text-sm text-slate-500">
            Post more trips — the more you post, the more senders you'll match with.
          </div>
        </div>
      )}
      {items.map((row) => (
        <div key={row.parcel.id} className="rounded-2xl bg-white border border-slate-200 p-4" data-testid={`parcel-row-${row.parcel.id}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="font-display font-bold text-slate-900 truncate">
                {row.parcel.pickup_location} → {row.parcel.drop_location}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {sizeLabel(row.parcel)}
                {row.parcel.category ? ` · ${row.parcel.category}` : ""}
              </div>
              <div className="mt-1 flex items-center gap-2 flex-wrap text-[11px]">
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 px-2 py-0.5">
                  <Route className="w-3 h-3"/> {Number(row.detour_km || 0).toFixed(1)} km detour
                </span>
                <span className="text-slate-500">
                  {row.sender?.name || "Sender"}
                  {row.sender?.rating_avg > 0 && <>
                    {" · "}
                    <Star className="inline w-3 h-3 fill-amber-400 stroke-amber-500"/> {row.sender.rating_avg}
                  </>}
                </span>
                <span className="text-slate-400">
                  departs {row.trip?.date || "soon"}{row.trip?.departure_time ? ` · ${String(row.trip.departure_time).includes("T") ? new Date(row.trip.departure_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : row.trip.departure_time}` : ""}
                </span>
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="font-display text-2xl font-extrabold text-slate-900">₹{Number(row.quote?.carrier_payout || 0).toFixed(0)}</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">You earn</div>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {row.existing_request ? (
              <span data-testid={`already-offered-${row.parcel.id}`} className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 text-[11px] font-medium px-2.5 py-1">
                <Timer className="w-3 h-3"/> {row.existing_request.initiated_by === "carrier" ? "Offer sent" : "Request pending"}
              </span>
            ) : (
              <Button
                size="sm"
                onClick={() => offer(row)}
                disabled={offering === row.parcel.id}
                className="rounded-full ml-auto bg-blue-600 hover:bg-blue-700 text-white"
                data-testid={`offer-btn-${row.parcel.id}`}
              >
                {offering === row.parcel.id ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <><Send className="w-3.5 h-3.5 mr-1"/> Offer to deliver</>}
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AvailableParcels;
